import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { ISliderValue } from '../multi-select-options/multi-select-options';

interface ISliderProgressProps {
  totalPercentage: number;
  sliderValues: ISliderValue[];
  disabled: boolean;
}

export const SliderProgress = ({
  totalPercentage,
  sliderValues,
  disabled,
}: ISliderProgressProps) => {
  const { t } = useTranslation();
  const id = useId();
  const labelId = `progress-label-${id}`;

  const remainingPercentage = Number.parseFloat(
    (100 - totalPercentage).toFixed(2)
  );

  const hasError = sliderValues.length > 0 && totalPercentage !== 100;
  const isDisabled = sliderValues.length === 0 || disabled;

  return (
    <ProgressContainer>
      <ProgressLabelsContainer>
        <StyledTypography
          id={labelId}
          disabled={isDisabled}
          variant={TypographyVariant.bodySm2}
        >
          {t('benefits.totalPercentageAllocated')}
        </StyledTypography>
        <StyledTypography
          variant={TypographyVariant.bodySm2}
          hasError={hasError}
          disabled={isDisabled}
        >
          {totalPercentage}%
        </StyledTypography>
      </ProgressLabelsContainer>
      <ProgressBar
        role="progressbar"
        aria-labelledby={labelId}
        aria-valuenow={totalPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <ProgressBarInner width={totalPercentage} disabled={isDisabled} />
      </ProgressBar>
      <RemainingAmountContainer>
        <StyledTypography
          variant={TypographyVariant.bodySm1}
          hasError={hasError}
          disabled={isDisabled}
        >
          {t('benefits.remainingPercentage', {
            percentage: remainingPercentage,
          })}
        </StyledTypography>
      </RemainingAmountContainer>
    </ProgressContainer>
  );
};

const ProgressContainer = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const ProgressLabelsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: ${theme.spacing(1)};
  `}
`;

const ProgressBar = styled.div`
  ${({ theme }) => css`
    height: 4px;
    border-radius: ${theme.borderRadius.sm};
    background-color: ${theme.palette.primary.bg};
    margin-bottom: ${theme.spacing(1)};
    overflow: hidden;
  `}
`;

const ProgressBarInner = styled.div<{
  width: number;
  disabled?: boolean;
}>`
  ${({ theme, width, disabled }) => css`
    width: ${width}%;
    height: 4px;
    border-radius: ${theme.borderRadius.sm};
    transition:
      width 0.5s,
      opacity 0.5s;
    opacity: ${width === 0 ? 0 : 1};
    background-color: ${disabled
      ? theme.palette.neutral[80]
      : theme.palette.primary.main};
  `}
`;

const RemainingAmountContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spacing(0.5)};
  `}
`;

const StyledTypography = styled(Typography)<{
  hasError?: boolean;
  disabled?: boolean;
}>`
  ${({ theme, hasError, disabled }) => {
    if (disabled) {
      return css`
        color: ${theme.palette.neutral[80]};
      `;
    }
    if (hasError) {
      return css`
        color: ${theme.palette.error.main};
      `;
    }

    return css`
      color: ${theme.palette.neutral[100]};
    `;
  }};
`;

export default SliderProgress;
